import { yupResolver } from '@hookform/resolvers/yup'
import React, { ReactElement } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { CreditApplication, LoanPurposeInput, LoanPurposeInputSchema } from '../../data/schemas/PrequalificationSchema'
import { useAppSelector } from '../../data/store'
import { getValuesFromObject, validateObjectValue } from '../utils/utils'
import { creditApplicationSelectors } from '../../data/store/CreditApplicationStore'
import { Enum } from '../../data/types/constants'
import { dynamicObject } from '../InitialFinancingInformationContainer/InitialFinancingInformationContainer'
import Alert from '../components/Alert'

type Props = {
  stepProgression: ReactElement
  creditApplication: CreditApplication
  onFormSubmitted: (data: dynamicObject) => void
  getFooter: (handleBack: () => void) => ReactElement
  handleBack: (data: dynamicObject) => void
}

function LoanPurposeForm({
  getFooter: footer,
  handleBack,
  stepProgression,
  onFormSubmitted,
  creditApplication,
}: Props) {
  const { t } = useTranslation()
  const currentMerchantId = useAppSelector(creditApplicationSelectors.getCurrentMerchantId)
  const isIfinanceMerchant = currentMerchantId === Number(process.env.REACT_APP_IFINANCE_MERCHANT_ID)

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    getValues,
    reset,
    trigger,
  } = useForm<LoanPurposeInput>({
    mode: 'onBlur',
    resolver: yupResolver(LoanPurposeInputSchema),
  })

  React.useEffect(() => {
    const defaultValues = getValuesFromObject(Object.keys(LoanPurposeInputSchema.getDefault()), creditApplication)
    reset(defaultValues)
    validateObjectValue(getValues(), trigger)
  }, [creditApplication])

  const bottomRef = React.useRef<HTMLDivElement | null>(null)

  const radioChange = (event: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
    setValue('loanPurposeId', event.currentTarget.value)
    errors.loanPurposeId = undefined
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const goBack = () => {
    handleBack(getValues())
  }

  return (
    <main className="form-section step-2-active">
      {stepProgression}
      <section className="step-content">
        <h1 className="h2" style={{ textAlign: 'center' }}>
          {t('loanPurpose.purpose')}
        </h1>

        {isIfinanceMerchant && (
          <div className="paragraph">
            <p>{t('loanPurpose.sharePurpose')}</p>
          </div>
        )}

        <form onSubmit={handleSubmit(onFormSubmitted)}>
          {errors.loanPurposeId && <Alert type="error" message={t<string>('common.loanPurposeSelectError')} />}

          {isIfinanceMerchant && (
            <div className="control-group">
              <div className="radiobuttons-wrap with-icon">
                <div className="radio-wrap">
                  <input
                    type="radio"
                    value={Enum.ELoanPurpose.Medical}
                    id={Enum.ELoanPurpose.Medical}
                    checked={watch('loanPurposeId') === Enum.ELoanPurpose.Medical}
                    name="loanPurpose"
                    onClick={radioChange}
                  />
                  <label htmlFor={Enum.ELoanPurpose.Medical}>
                    <i className="fa-light fa-hospital" />
                    <span>{t<string>('enum.eLoanPurpose.medical')}</span>
                  </label>
                </div>
                <div className="radio-wrap">
                  <input
                    type="radio"
                    value={Enum.ELoanPurpose.Dentistry}
                    id={Enum.ELoanPurpose.Dentistry}
                    checked={watch('loanPurposeId') === Enum.ELoanPurpose.Dentistry}
                    name="loanPurpose"
                    onClick={radioChange}
                  />
                  <label htmlFor={Enum.ELoanPurpose.Dentistry}>
                    <i className="fa-light fa-tooth" />
                    <span>{t<string>('enum.eLoanPurpose.dentistry')}</span>
                  </label>
                </div>
                <div className="radio-wrap">
                  <input
                    type="radio"
                    value={Enum.ELoanPurpose.Veterinary}
                    id={Enum.ELoanPurpose.Veterinary}
                    checked={watch('loanPurposeId') === Enum.ELoanPurpose.Veterinary}
                    name="loanPurpose"
                    onClick={radioChange}
                  />
                  <label htmlFor={Enum.ELoanPurpose.Veterinary} id={Enum.ELoanPurpose.Veterinary}>
                    <i className="fa-sharp fa-light fa-dog" />
                    <span>{t<string>('enum.eLoanPurpose.veterinary')}</span>
                  </label>
                </div>
                <div className="radio-wrap">
                  <input
                    type="radio"
                    value={Enum.ELoanPurpose.HomeImprovement}
                    id={Enum.ELoanPurpose.HomeImprovement}
                    checked={watch('loanPurposeId') === Enum.ELoanPurpose.HomeImprovement}
                    name="loanPurpose"
                    onClick={radioChange}
                  />
                  <label htmlFor={Enum.ELoanPurpose.HomeImprovement}>
                    <i className="fa-light fa-house" />
                    <span>{t<string>('enum.eLoanPurpose.homeImprovement')}</span>
                  </label>
                </div>
                <div className="radio-wrap">
                  <input
                    type="radio"
                    value={Enum.ELoanPurpose.GoodsAndServices}
                    id={Enum.ELoanPurpose.GoodsAndServices}
                    checked={watch('loanPurposeId') === Enum.ELoanPurpose.GoodsAndServices}
                    name="loanPurpose"
                    onClick={radioChange}
                  />
                  <label htmlFor={Enum.ELoanPurpose.GoodsAndServices}>
                    <i className="fa-light fa-loveseat" />
                    <span>{t<string>('enum.eLoanPurpose.goodsAndServices')}</span>
                  </label>
                </div>
                <div className="radio-wrap">
                  <input
                    type="radio"
                    value={Enum.ELoanPurpose.Other}
                    id={Enum.ELoanPurpose.Other}
                    checked={watch('loanPurposeId') === Enum.ELoanPurpose.Other}
                    name="loanPurpose"
                    onClick={radioChange}
                  />
                  <label htmlFor={Enum.ELoanPurpose.Other}>
                    <i className="fa-light fa-envelope-open-dollar" />
                    <span>{t<string>('enum.eLoanPurpose.other')}</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {footer(goBack)}
          <div ref={bottomRef}> </div>
        </form>
      </section>
    </main>
  )
}

export default LoanPurposeForm
